import { createContext, useContext, useEffect, useState } from "react";
import {
  hasDecided,
  getConsent,
  saveConsent,
  acceptAll,
  rejectAll,
  syncStoredConsent,
  CONSENT_CATEGORIES,
} from "../../lib/cookieConsent";
import CookieConsentBanner from "./CookieConsentBanner";
import CookieConsentModal from "./CookieConsentModal";
import "../../styles/cookieConsent.css";

function defaultSelection() {
  const selection = {};
  CONSENT_CATEGORIES.forEach((category) => {
    selection[category.id] = category.required;
  });
  return selection;
}

const CookieConsentContext = createContext({
  openPreferences: () => {},
});

// Footer's "Cookie beállítások" link (and anything else that needs it) reads
// this to reopen the preference modal after the initial decision.
export function useCookieConsent() {
  return useContext(CookieConsentContext);
}

/**
 * Owns the one decision that matters (has the visitor chosen yet?) and mounts
 * the banner/modal as siblings of the app, not wrappers around it — neither
 * renders anything once a decision exists, except when explicitly reopened
 * via openPreferences (the footer entry point).
 */
export default function CookieConsentProvider({ children }) {
  const [decided, setDecided] = useState(() => hasDecided());
  const [modalOpen, setModalOpen] = useState(false);
  const [initialSelection, setInitialSelection] = useState(
    () => getConsent()?.categories || defaultSelection(),
  );

  // index.html's inline GTM snippet always boots marketing consent (Ads,
  // Meta) as "denied" — analytics_storage is granted unconditionally there
  // and isn't affected by this. For a returning visitor who already
  // decided, replay that decision into the dataLayer now (fallback for the
  // inline sync in index.html, e.g. if a stale cached copy of it is served)
  // so Ads/Meta tags aren't stuck denied all session.
  useEffect(() => {
    syncStoredConsent();
  }, []);

  const openPreferences = () => {
    setInitialSelection(getConsent()?.categories || defaultSelection());
    setModalOpen(true);
  };

  const handleAcceptAll = () => {
    acceptAll();
    setDecided(true);
    setModalOpen(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setDecided(true);
    setModalOpen(false);
  };

  const handleSave = (categories) => {
    saveConsent(categories);
    setDecided(true);
    setModalOpen(false);
  };

  return (
    <CookieConsentContext.Provider value={{ openPreferences }}>
      {children}
      {!decided && !modalOpen && (
        <CookieConsentBanner
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onOpenPreferences={openPreferences}
        />
      )}
      {modalOpen && (
        <CookieConsentModal
          initialSelection={initialSelection}
          onAcceptAll={handleAcceptAll}
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
        />
      )}
    </CookieConsentContext.Provider>
  );
}
